import { ReactNode } from "react";
import dayjs from "dayjs";
import { NormasTeachingItemType } from "../../Stripe/StripeOrderDTO.ts";
import { PurchasedItemRef } from "../../ProductService/PurchasedItem/resolvePurchasedItemName.ts";
import { usePurchasedItemNames } from "../../ProductService/PurchasedItem/usePurchasedItemNames.ts";
import { CopyableText } from "../../UI/CopyableText.tsx";
import { TableColumnHeader } from "../../UI/Table/TableColumnHeader.tsx";
import { DateRangeFilter } from "../../UI/Table/Filters/DateRangeFilter.tsx";
import { AmountRangeFilter } from "../../UI/Table/Filters/AmountRangeFilter.tsx";
import {
  CheckboxListFilter,
  CheckboxListOption,
} from "../../UI/Table/Filters/CheckboxListFilter.tsx";
import { SortDirection } from "../../UI/Table/listUrlParams.ts";
import { PurchasedItemFilter } from "../OrderFilters/PurchasedItemFilter.tsx";
import { CouponColumnFilter } from "../OrderFilters/CouponColumnFilter.tsx";
import {
  OrderSortField,
  OrdersFilters,
  OrdersListQuery,
  pickOrdersFilters,
} from "./OrdersListQuery.ts";

export type OrdersTableRow<Status extends string> = {
  id: string;
  createdAt?: string;
  customer: { firstName?: string; lastName?: string; email: string };
  purchasedItem: PurchasedItemRef;
  chargedAmountInCents: number;
  couponCode?: string;
  status: Status;
};

export type OrdersTableExtraColumn<Order, Status extends string> = {
  key: string;
  renderHeader: (
    filters: OrdersFilters<Status>,
    updateFilters: (changes: OrdersFilters<Status>) => void,
  ) => ReactNode;
  renderCell: (order: Order) => ReactNode;
};

export function OrdersTable<Order, Status extends string>({
  orders,
  toRow,
  query,
  onSort,
  onFiltersChange,
  statusOptions,
  renderStatus,
  extraColumns = [],
  renderActions,
  onSelectOrder,
}: {
  orders: Order[];
  toRow: (order: Order) => OrdersTableRow<Status>;
  query: OrdersListQuery<Status>;
  onSort: (field: OrderSortField, direction: SortDirection) => void;
  onFiltersChange: (filters: OrdersFilters<Status>) => void;
  statusOptions: CheckboxListOption<Status>[];
  renderStatus: (status: Status) => ReactNode;
  extraColumns?: OrdersTableExtraColumn<Order, Status>[];
  renderActions?: (order: Order) => ReactNode;
  onSelectOrder: (order: Order) => void;
}) {
  const { resolveName } = usePurchasedItemNames();
  const filters = pickOrdersFilters(query);
  const updateFilters = (changes: OrdersFilters<Status>) =>
    onFiltersChange({ ...filters, ...changes });
  const sortProps = (field: OrderSortField) => ({
    sortDirection: query.sortBy === field ? query.sortDirection : undefined,
    onSort: (direction: SortDirection) => onSort(field, direction),
  });
  const columnsCount = 6 + extraColumns.length + (renderActions ? 1 : 0);

  return (
    <div className="overflow-x-auto max-w-full">
      <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
        <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
          <tr>
            <th className="pl-6 pr-2 py-3">
              <TableColumnHeader
                label="Data creazione"
                {...sortProps("createdAt")}
                sortLabels={{
                  asc: "Dal meno recente",
                  desc: "Dal più recente",
                }}
                isFiltered={!!(filters.createdFrom || filters.createdTo)}
                renderFilter={(close) => (
                  <DateRangeFilter
                    value={{ from: filters.createdFrom, to: filters.createdTo }}
                    onApply={({ from, to }) =>
                      updateFilters({ createdFrom: from, createdTo: to })
                    }
                    close={close}
                  />
                )}
              />
            </th>
            <th className="px-2 py-3">
              <TableColumnHeader
                label="Cliente"
                {...sortProps("customer")}
                sortLabels={{ asc: "A → Z", desc: "Z → A" }}
              />
            </th>
            <th className="px-2 py-3">
              <TableColumnHeader
                label="Prodotto"
                isPanelWide
                isFiltered={!!filters.productIds?.length}
                renderFilter={(close) => (
                  <PurchasedItemFilter
                    value={filters.productIds ?? []}
                    onApply={(productIds) => updateFilters({ productIds })}
                    close={close}
                  />
                )}
              />
            </th>
            <th className="px-2 py-3">
              <TableColumnHeader
                label="Importo"
                {...sortProps("amount")}
                sortLabels={{ asc: "Dal più basso", desc: "Dal più alto" }}
                isFiltered={
                  filters.amountMinInCents !== undefined ||
                  filters.amountMaxInCents !== undefined
                }
                renderFilter={(close) => (
                  <AmountRangeFilter
                    value={{
                      minInCents: filters.amountMinInCents,
                      maxInCents: filters.amountMaxInCents,
                    }}
                    onApply={({ minInCents, maxInCents }) =>
                      updateFilters({
                        amountMinInCents: minInCents,
                        amountMaxInCents: maxInCents,
                      })
                    }
                    close={close}
                  />
                )}
              />
            </th>
            {extraColumns.map((column) => (
              <th key={column.key} className="px-2 py-3">
                {column.renderHeader(filters, updateFilters)}
              </th>
            ))}
            <th className="px-2 py-3">
              <TableColumnHeader
                label="Coupon"
                isFiltered={!!filters.coupon}
                renderFilter={(close) => (
                  <CouponColumnFilter
                    value={filters.coupon}
                    onApply={(coupon) => updateFilters({ coupon })}
                    close={close}
                  />
                )}
              />
            </th>
            <th className="px-2 py-3">
              <TableColumnHeader
                label="Stato"
                isFiltered={!!filters.statuses?.length}
                renderFilter={(close) => (
                  <CheckboxListFilter
                    options={statusOptions}
                    value={filters.statuses ?? []}
                    onApply={(statuses) => updateFilters({ statuses })}
                    close={close}
                  />
                )}
              />
            </th>
            {renderActions && <th className="w-px pl-2 pr-4 py-3">Azioni</th>}
          </tr>
        </thead>
        <tbody>
          {orders.length === 0 && (
            <tr className="bg-white">
              <td
                colSpan={columnsCount}
                className="px-2 py-10 text-center text-gray-500"
              >
                Nessun ordine trovato.
              </td>
            </tr>
          )}
          {orders.map((order) => {
            const row = toRow(order);
            const productName = resolveName(row.purchasedItem).name;
            const fullName =
              `${row.customer.firstName ?? ""} ${row.customer.lastName ?? ""}`.trim();
            return (
              <tr
                key={row.id}
                className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 border-gray-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600"
                onClick={() => onSelectOrder(order)}
              >
                <td className="pl-6 pr-2 py-3 whitespace-nowrap">
                  {row.createdAt ? (
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {dayjs(row.createdAt).format("DD/MM/YYYY")}
                      </span>
                      <span className="text-gray-500 dark:text-gray-400 text-xs">
                        {dayjs(row.createdAt).format("HH:mm:ss")}
                      </span>
                    </div>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-2 py-3 max-w-[160px]">
                  <div className="font-medium text-gray-900 dark:text-white truncate">
                    {fullName || "N/A"}
                  </div>
                  <div
                    className="text-gray-500 dark:text-gray-400 text-xs truncate"
                    title={row.customer.email}
                  >
                    {row.customer.email}
                  </div>
                </td>
                <td className="px-2 py-3 max-w-[120px] truncate">
                  <CopyableText
                    className="cursor-pointer hover:text-blue-600"
                    title={productName}
                    value={row.purchasedItem.id}
                    copyMessage={`ID del ${row.purchasedItem.type === NormasTeachingItemType.Bundle ? "bundle" : "prodotto"} copiato negli appunti!`}
                  >
                    {productName}
                  </CopyableText>
                </td>
                <td className="px-2 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                  €{(row.chargedAmountInCents / 100).toFixed(2)}
                </td>
                {extraColumns.map((column) => (
                  <td key={column.key} className="px-2 py-3 whitespace-nowrap">
                    {column.renderCell(order)}
                  </td>
                ))}
                <td className="px-2 py-3 max-w-[100px] truncate">
                  {row.couponCode ? (
                    <span title={row.couponCode}>{row.couponCode}</span>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-2 py-3 whitespace-nowrap text-xs">
                  {renderStatus(row.status)}
                </td>
                {renderActions && (
                  <td
                    className="w-px pl-2 pr-4 py-3 whitespace-nowrap"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {renderActions(order)}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
